import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { DeleteOutlined, EditOutlined, TeamOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/react-hooks";
import * as Sentry from "@sentry/browser";
import { Alert, Button, Dropdown, Form, Menu, message, Modal } from "antd";
import { connect } from "formik";
import gql from "fraql";
import find from "lodash/find";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import omit from "lodash/omit";
import { getCookieValue } from "../../../../../utils/cookies";
import { serverErrorStatus } from "../../../../../utils/graphqlErrors";
import useCurrentUser from "../../../../../utils/useCurrentUser";
import { useGetUserHasPermission, PERMISSION } from "../../../../../utils/useGetUserHasPermission";
import { useRegContext } from "../../../../../utils/useRegContext";
import { useAuth0 } from "../../../../Auth0";
import ButtonMore from "../../../../ButtonMore";
import DrawerCreateTeam from "../../../../DrawerCreateTeam";
import DrawerUpdateTeam from "../../../../DrawerUpdateTeam";
import FormFieldFormItem from "../../../../FormFields/FormFieldFormItem";
import FormFieldTeam, { isFull } from "../../../../FormFields/FormFieldTeam";
import FormFieldTeamPosition from "../../../../FormFields/FormFieldTeamPosition";
import TeamQualificationScoreNotProvidedAlert from "../../../Competitions/TeamQualificationScoreNotProvidedAlert";
import ViewTeamDrawer from "../../../Competitions/ViewTeamDrawer/ViewTeamDrawer";
import "./RegistrationStepCompetitionsFormFieldTeam.scss";

const GET_TEAMS_FOR_COMPETITION = gql`
  query RegistrationStepCompetitionsFormFieldTeam_GetTeams($CompetitionId: String!) {
    Team(where: { CompetitionId: { _eq: $CompetitionId } }, order_by: { name: asc }) {
      id
      name
      qualificationScore
      Competition {
        id
        qualificationScoreType
        qualificationScoreRequired
        teamQualificationScoreDetermination
        maxTeamMembers
      }
      TeamPeople {
        PersonId
      }
      CreatedBy {
        id
      }
      TeamPeople_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

const DELETE_TEAM = gql`
  mutation RegistrationStepCompetitionsFormFieldTeam_DeleteTeam($TeamId: ID!) {
    deleteTeam(TeamId: $TeamId) {
      affected_rows
    }
  }
`;

const GET_QUERY_ROOT_FIELDS = gql`
  query RegistrationStepCompetitionsFormFieldTeam_GetQueryRootFields {
    __type(name: "query_root") {
      fields {
        name
      }
    }
  }
`;

const GET_INVITED_TEAM_IDS_FOR_USER_IN_COMPETITION = gql`
  query RegistrationStepCompetitionsFormFieldTeam_GetInvitedTeamIdsForUserInCompetition(
    $UserEmail: String!
    $CompetitionId: String!
  ) {
    getInvitedTeamIdsForUserInCompetition(UserEmail: $UserEmail, CompetitionId: $CompetitionId) {
      affected_rows
      TeamIds
    }
  }
`;

function RegistrationStepCompetitionsFormFieldTeam(props) {
  const {
    name,
    disabled,
    competitionId,
    registrantsCanCreateTeams,
    registrantsCanCreateTeamsRoles,
    managersCanCreateTeams,
    teamSelectionRequired,
    teamPositionRequired,
    formik: { setFieldValue, setFieldTouched, values },
  } = props;

  const teamFieldName = `${name}.team`;
  const teamFieldValue = get(values, teamFieldName, undefined);

  const teamPositionFieldName = `${name}.teamPosition`;

  const currentUser = useCurrentUser();
  const { user } = useAuth0();
  const userEmail = get(user, "email", null);
  const invitedTeamIdFromCookie = useMemo(() => getCookieValue("InvitedToTeam"), []);
  const regContext = useRegContext();
  const regId = get(regContext, "regContextData.RegId");
  const userRegSubmissionParticipationRoleId = get(regContext, "regContextData.RegSubmission.RoleId", null);
  const hasFullAccessToManageTeam = useGetUserHasPermission(PERMISSION.MANAGE_TEAM);
  const hasConditionalAccessToManageTeam = useGetUserHasPermission(PERMISSION.MANAGE_TEAM_CONDITIONALLY);
  const hasRegistrantAccessToManageTeam = useGetUserHasPermission(PERMISSION.MANAGE_TEAM_REGISTRANT);
  const hasPermissionToShowViewTeamButton = useGetUserHasPermission(PERMISSION.VIEW_TEAM);

  const [isDrawerCreateTeamVisible, setIsDrawerCreateTeamVisible] = useState(false);
  const [isDrawerUpdateTeamVisible, setIsDrawerUpdateTeamVisible] = useState(false);
  const [isViewTeamDrawerVisible, setIsViewTeamDrawerVisible] = useState(false);
  const [viewTeamDrawerInitialTabKey, setViewTeamDrawerInitialTabKey] = useState(undefined);

  const { error: teamsError, data: teamsData, refetch: teamsRefetch } = useQuery(GET_TEAMS_FOR_COMPETITION, {
    variables: { CompetitionId: competitionId },
  });

  const { data: queryRootFieldsData, error: queryRootFieldsError } = useQuery(GET_QUERY_ROOT_FIELDS);

  const hasGetInvitedTeamIdsForUserInCompetitionAction = useMemo(() => {
    if (queryRootFieldsError) {
      return false;
    }

    const queryRootFields = get(queryRootFieldsData, "__type.fields", []);

    return !!find(queryRootFields, { name: "getInvitedTeamIdsForUserInCompetition" });
  }, [queryRootFieldsData, queryRootFieldsError]);

  const {
    data: invitedTeamIdsData,
  } = useQuery(GET_INVITED_TEAM_IDS_FOR_USER_IN_COMPETITION, {
    variables: {
      UserEmail: userEmail,
      CompetitionId: competitionId,
    },
    skip: isEmpty(userEmail) || !hasGetInvitedTeamIdsForUserInCompetitionAction || !isEmpty(invitedTeamIdFromCookie),
  });

  const effectiveInvitedTeamIds = useMemo(() => {
    if (!isEmpty(invitedTeamIdFromCookie)) {
      return [invitedTeamIdFromCookie];
    }

    return get(invitedTeamIdsData, "getInvitedTeamIdsForUserInCompetition.TeamIds", []);
  }, [invitedTeamIdFromCookie, invitedTeamIdsData]);

  const invitedTeamNames = useMemo(() => {
    const teams = get(teamsData, "Team", []);

    if (isEmpty(effectiveInvitedTeamIds) || isEmpty(teams)) {
      return [];
    }

    return teams.filter(team => effectiveInvitedTeamIds.includes(team.id)).map(team => team.name);
  }, [effectiveInvitedTeamIds, teamsData]);

  const invitedTeamsMessage = useMemo(() => {
    if (isEmpty(invitedTeamNames)) {
      return null;
    }

    if (invitedTeamNames.length === 1) {
      return `You have been invited to join ${invitedTeamNames[0]}.`;
    }

    return `You have been invited to join: ${invitedTeamNames.join(", ")}.`;
  }, [invitedTeamNames]);

  const invitedTeamIdToAutoSelect = useMemo(() => {
    const teams = get(teamsData, "Team", []);

    if (isEmpty(effectiveInvitedTeamIds) || isEmpty(teams)) {
      return undefined;
    }

    const invitedTeam = find(teams, team => effectiveInvitedTeamIds.includes(team.id));

    return get(invitedTeam, "id", undefined);
  }, [effectiveInvitedTeamIds, teamsData]);

  useEffect(() => {
    if (!isEmpty(teamFieldValue)) {
      return;
    }

    if (isEmpty(invitedTeamIdToAutoSelect)) {
      return;
    }

    setFieldValue(teamFieldName, invitedTeamIdToAutoSelect);
  }, [teamFieldValue, invitedTeamIdToAutoSelect, setFieldValue, teamFieldName]);

  const teamSelected = useMemo(() => {
    if (!teamFieldValue) {
      // Match return value of `find` function when team is not found in `teams`.
      return undefined;
    }

    const teams = get(teamsData, "Team", []);

    const result = find(teams, { id: teamFieldValue });

    if (result && isFull(result)){
      return undefined;
    }

    return result;
  }, [teamsData, teamFieldValue]);

  const [deleteTeam] = useMutation(DELETE_TEAM);

  const handleCloseDrawerCreateTeam = useCallback(() => {
    setIsDrawerCreateTeamVisible(false);
  }, [setIsDrawerCreateTeamVisible]);

  const handleCloseDrawerUpdateTeam = useCallback(() => {
    setIsDrawerUpdateTeamVisible(false);
  }, [setIsDrawerUpdateTeamVisible]);

  const handleMutationsTeamSuccess = useCallback(
    async newTeamId => {
      setIsDrawerCreateTeamVisible(false);
      setIsDrawerUpdateTeamVisible(false);

      await teamsRefetch();

      setFieldValue(teamFieldName, newTeamId);
      setFieldTouched(teamFieldName);
    },
    [
      teamFieldName,
      setIsDrawerCreateTeamVisible,
      setIsDrawerUpdateTeamVisible,
      teamsRefetch,
      setFieldValue,
      setFieldTouched,
    ],
  );

  const handleCreateTeamSuccess = useCallback(
    async newTeamId => {
      await handleMutationsTeamSuccess(newTeamId);
    },
    [handleMutationsTeamSuccess],
  );

  const handleUpdateTeamSuccess = useCallback(
    async newTeamId => {
      await handleMutationsTeamSuccess(newTeamId);
    },
    [handleMutationsTeamSuccess],
  );

  const handleViewTeamClick = useCallback(initialTabKey => {
    setViewTeamDrawerInitialTabKey(initialTabKey);
    setIsViewTeamDrawerVisible(true);
  }, [setViewTeamDrawerInitialTabKey, setIsViewTeamDrawerVisible]);

  const handleInviteTeamMembersClick = useCallback(() => {
    handleViewTeamClick("invited");
  }, [handleViewTeamClick]);

  const handleCloseViewTeamDrawer = useCallback(() => {
    setIsViewTeamDrawerVisible(false);
    setViewTeamDrawerInitialTabKey(undefined);
  }, [setIsViewTeamDrawerVisible, setViewTeamDrawerInitialTabKey]);

  const handleDeleteTeam = useCallback(() => {
    const teamName = get(teamSelected, "name", "");

    if (!teamName) {
      return;
    }

    const content = `Are you sure you want to delete the team "${teamName}"? If any person has chosen it in their registration submission, it will not be deleted.`;

    const modal = Modal.confirm({
      title: "Delete team from competition",
      content,
      okText: "Ok",
      async onOk() {
        modal.update({
          okButtonProps: {
            loading: true,
          },
          cancelButtonProps: {
            disabled: true,
          },
        });

        try {
          await deleteTeam({
            variables: { TeamId: teamSelected.id },
          });

          await handleMutationsTeamSuccess("");

          message.success("Team deleted.");
        } catch (error) {
          console.error(error);

          const errorStatus = serverErrorStatus(error);

          const description = get(
            errorStatus,
            "description",
            "There was an error processing your request. Please try again later.",
          );

          Modal.error({
            title: "Team not deleted",
            content: description,
          });

          Sentry.captureException(error);
        }
      },
      onCancel() {},
    });
  }, [deleteTeam, teamSelected, handleMutationsTeamSuccess]);

  const userCanManageTeam = useMemo(() => {
    if (hasFullAccessToManageTeam) {
      return true;
    }

    const teamCreatorId = get(teamSelected, "CreatedBy.id", null);

    if (isNull(teamCreatorId)) {
      return false;
    }

    return (
      (hasConditionalAccessToManageTeam || hasRegistrantAccessToManageTeam) && get(currentUser, "id") === teamCreatorId
    );
  }, [
    hasFullAccessToManageTeam,
    hasConditionalAccessToManageTeam,
    hasRegistrantAccessToManageTeam,
    currentUser,
    teamSelected,
  ]);

  const showInviteTeamMembersButton = useMemo(() => {
    return !isUndefined(teamSelected) && userCanManageTeam;
  }, [teamSelected, userCanManageTeam]);

  const dropdownMarkup = useMemo(() => {
    if (isUndefined(teamSelected)) {
      return null;
    }

    const menuItems = [];

    if (hasPermissionToShowViewTeamButton) {
      menuItems.push(
        <Menu.Item key="0" onClick={handleViewTeamClick}>
          <TeamOutlined />
          View Team
        </Menu.Item>,
      );
    }

    if (userCanManageTeam) {
      menuItems.push(
        <Menu.Item key="1" onClick={() => setIsDrawerUpdateTeamVisible(true)}>
          <EditOutlined />
          Edit
        </Menu.Item>,
        <Menu.Item key="2" onClick={handleDeleteTeam}>
          <DeleteOutlined />
          Delete
        </Menu.Item>,
      );
    }

    if (isEmpty(menuItems)) {
      return null;
    }

    const menu = <Menu theme="dark">{menuItems}</Menu>;

    return (
      <>
        <Dropdown overlay={menu} trigger={["click"]}>
          <ButtonMore className="form-field-team__button" />
        </Dropdown>

        <DrawerUpdateTeam
          isVisible={isDrawerUpdateTeamVisible && !disabled}
          handleClose={handleCloseDrawerUpdateTeam}
          handleSuccess={handleUpdateTeamSuccess}
          teamId={teamSelected.id}
        />

        <ViewTeamDrawer
          visible={isViewTeamDrawerVisible}
          teamId={teamFieldValue}
          regId={regId}
          handleClose={handleCloseViewTeamDrawer}
          initialTabKey={viewTeamDrawerInitialTabKey}
        />
      </>
    );
  }, [
    handleDeleteTeam,
    teamSelected,
    setIsDrawerUpdateTeamVisible,
    disabled,
    isDrawerUpdateTeamVisible,
    handleCloseDrawerUpdateTeam,
    isViewTeamDrawerVisible,
    teamFieldValue,
    regId,
    handleCloseViewTeamDrawer,
    viewTeamDrawerInitialTabKey,
    hasPermissionToShowViewTeamButton,
    handleViewTeamClick,
    userCanManageTeam,
    handleUpdateTeamSuccess,
  ]);

  const canCreateTeam = useMemo(() => {
    const allowedRegistrantRoleIds = registrantsCanCreateTeamsRoles
      .map(roleItem => get(roleItem, "Role.id", get(roleItem, "RoleId", get(roleItem, "roleId", null))))
      .filter(roleId => !isNull(roleId) && !isUndefined(roleId));

    const hasMatchingRegistrantRole = allowedRegistrantRoleIds.includes(userRegSubmissionParticipationRoleId);

    const canCreateFromRegistrantRole =
      registrantsCanCreateTeams && hasRegistrantAccessToManageTeam && hasMatchingRegistrantRole;

    if (hasFullAccessToManageTeam) {
      return true;
    }

    if (hasConditionalAccessToManageTeam && managersCanCreateTeams) {
      return true;
    }

    if (canCreateFromRegistrantRole) {
      return true;
    }

    return false;
  }, [
    hasFullAccessToManageTeam,
    hasConditionalAccessToManageTeam,
    hasRegistrantAccessToManageTeam,
    managersCanCreateTeams,
    registrantsCanCreateTeams,
    registrantsCanCreateTeamsRoles,
    userRegSubmissionParticipationRoleId,
  ]);

  if (teamsError) {
    return (
      <FormFieldFormItem {...omit(props, ["formik"])} displayForInput={false}>
        <Alert message="Teams failed to load" type="error" className="form-field-alert" />
      </FormFieldFormItem>
    );
  }

  return (
    <>
      <>
        <div className="form-field-team">
          <div className="form-field-team__container">
            <div className="form-field-team__field">
              <FormFieldTeam
                {...omit(props, ["formik"])}
                meta={{
                  required: teamSelectionRequired,
                  label: "Team",
                  placeholder: "Select a team",
                  allowClear: true,
                }}
                competitionId={competitionId}
                name={teamFieldName}
                query={GET_TEAMS_FOR_COMPETITION}
                queryOptions={{ variables: { CompetitionId: competitionId } }}
                hideOnEmpty
                hideOnLoading
                personId={regContext.regContextData.PersonId}
              />

              {!isEmpty(invitedTeamsMessage) && (
                <Alert
                  message={invitedTeamsMessage}
                  type="info"
                  showIcon
                  className="form-field-team__invited-alert"
                />
              )}
            </div>

            {dropdownMarkup}
          </div>
          {teamSelected !== undefined && (
            <div className="form-field-team__position">
              <FormFieldTeamPosition
                {...omit(props, ["formik"])}
                meta={{
                  required: teamPositionRequired,
                  label: "Position",
                  placeholder: "Select a position",
                  allowClear: true,
                }}
                queryOptions={{ variables: { CompetitionId: competitionId } }}
                name={teamPositionFieldName}
                hideOnEmpty
                hideOnLoading
              />
            </div>
          )}
        </div>

        <TeamQualificationScoreNotProvidedAlert style={{ marginTop: 15 }} team={teamSelected} />
      </>

      {(canCreateTeam || showInviteTeamMembersButton) && (
        <div className="registration-step-competition-form-field-team__create-new">
          {showInviteTeamMembersButton && (
            <Form.Item className="registration-step-competition-form-field-team__invite-members">
              <Button
                className="registration-step-competition-form-field-team__invite-members-button"
                onClick={handleInviteTeamMembersClick}
                disabled={disabled}
              >
                Invite Team Members
              </Button>
            </Form.Item>
          )}

          {canCreateTeam && (
            <Form.Item label="Create your team">
              <Button onClick={() => setIsDrawerCreateTeamVisible(true)} disabled={disabled}>
                Create New
              </Button>
            </Form.Item>
          )}
        </div>
      )}

      <DrawerCreateTeam
        isVisible={isDrawerCreateTeamVisible && !disabled}
        handleClose={handleCloseDrawerCreateTeam}
        handleSuccess={handleCreateTeamSuccess}
        competitionId={competitionId}
        regId={regId}
      />
    </>
  );
}

RegistrationStepCompetitionsFormFieldTeam.propTypes = {
  name: PropTypes.string.isRequired,
  submitCount: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  competitionId: PropTypes.string.isRequired,
  registrantsCanCreateTeams: PropTypes.bool.isRequired,
  registrantsCanCreateTeamsRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      Role: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ).isRequired,
  managersCanCreateTeams: PropTypes.bool.isRequired,
  teamSelectionRequired: PropTypes.bool.isRequired,
  teamPositionRequired: PropTypes.bool.isRequired,
  formik: PropTypes.shape({
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    values: PropTypes.object,
  }).isRequired,
  readOnly: PropTypes.bool,
};

RegistrationStepCompetitionsFormFieldTeam.defaultProps = {
  readOnly: false,
};

export default connect(RegistrationStepCompetitionsFormFieldTeam);
